import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
// @mui
import {
  Box,
  Card,
  Chip,
  Stack,
  Button,
  Divider,
  Tooltip,
  Checkbox,
  Collapse,
  TextField,
  Typography,
  IconButton,
  Autocomplete,
  CircularProgress,
  FormControlLabel,
  Switch,
  MenuItem,
} from '@mui/material';
// components
import Iconify from '../../../components/Iconify';
// utils
import axiosInstance from '../../../utils/axios';
import { APIS } from '../../../utils/serviceUrls';

// ---------------------------------------------------------------------------
// Batches (cohorts) editor for a program.
//
// Membership lives on the program: each batch owns its `students` list. All
// batches share the same lectures/quizzes — the batch only separates who is in
// it, its dates, and the quiz analysis (see the per-batch report page).
// ---------------------------------------------------------------------------

type UserOption = { _id: string; name?: string; email?: string };

const labelOf = (o: UserOption) =>
  o?.name ? `${o.name}${o.email ? ` (${o.email})` : ''}` : o?.email || o?._id || '';

const emptyBatch = {
  name: '',
  name_ar: '',
  startDate: '',
  endDate: '',
  capacity: '',
  instructor: '',
  active: true,
  students: [] as string[],
};

// ---------------------------------------------------------------------------
// One batch's student picker.
//
// Its own component with its own input state, for a specific reason: MUI's
// useAutocomplete runs `useEffect(() => resetInputValue(null, value), [value])`,
// and for `multiple` that resets the text field to ''. So if `value` gets a new
// array identity on every render, every keystroke wipes what you typed. Here
// `value` is memoised on the selected ids + the resolved-label map, so it only
// changes when the selection actually changes.
// ---------------------------------------------------------------------------
function BatchStudentPicker({
  students,
  known,
  addKnown,
  duplicates,
  onChange,
}: {
  students: string[];
  known: Record<string, UserOption>;
  addKnown: (opts: UserOption[]) => void;
  duplicates: Set<string>;
  onChange: (ids: string[]) => void;
}) {
  const [options, setOptions] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');

  // Stable key for the selection, so the memo below ignores array re-creation.
  const studentsKey = students.join('|');

  const value = useMemo(
    () => students.map((id) => known[id] || { _id: id }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studentsKey, known]
  );

  // Debounced user search. Runs with an empty search too, so opening the picker
  // already lists users to assign instead of an empty menu.
  useEffect(() => {
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(APIS.USERS.PAGINATED, {
          params: { page: 1, pageSize: 20, search: input || undefined },
        });
        const payload = res.data?.data ?? res.data;
        const items: UserOption[] = Array.isArray(payload?.items)
          ? payload.items
          : Array.isArray(payload)
          ? payload
          : [];
        setOptions(items);
        // Deliberately NOT addKnown(items): search hits render from `options`,
        // and `known` is only needed to label SELECTED ids. Writing every result
        // into `known` would change its identity mid-typing, recompute `value`,
        // and trip MUI's reset-on-value-change — wiping the input again.
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [input]);

  return (
    <Autocomplete
      multiple
      size="small"
      options={options}
      loading={loading}
      filterSelectedOptions
      value={value}
      onChange={(_e, picked) => {
        // Store the picked users so their chips can render a name/email label.
        addKnown(picked as UserOption[]);
        onChange((picked as UserOption[]).map((p) => p._id));
      }}
      onInputChange={(_e, v) => setInput(v)}
      getOptionLabel={(o) => labelOf(o as UserOption)}
      isOptionEqualToValue={(o, v) => o._id === v._id}
      // The search is server-side; without this MUI ALSO filters the returned
      // users against the typed text and throws away valid matches.
      filterOptions={(x) => x}
      noOptionsText={loading ? 'Searching…' : 'No users found'}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option._id}
            size="small"
            label={labelOf(option as UserOption)}
            color={duplicates.has(option._id) ? 'warning' : 'default'}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Students in this batch"
          helperText="Students here can open the program. Search by name or email."
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// Per-batch content visibility: pick which lectures (and their quizzes) THIS
// batch does NOT see. Hidden content shows to the batch as "Opening soon".
//
// The canonical data lives on each lecture (lectures[i].excludedBatches and
// lectures[i].quiz.excludedBatches) — the same shape the student site + BE
// already read for quizzes. This control just presents it from the batch side:
// a checkbox toggles this batch's id in/out of the relevant lecture's array.
//
// Needs a saved batch id to reference, so it is only shown once the batch has
// an `_id` (i.e. after the program is saved once).
// ---------------------------------------------------------------------------
function BatchContentVisibility({
  batchId,
  lectures,
  onToggleLecture,
  onToggleQuiz,
  onSetAll,
}: {
  batchId: string;
  lectures: any[];
  // hide === true adds this batch to the excludedBatches; false removes it.
  onToggleLecture: (lectureIndex: number, hide: boolean) => void;
  onToggleQuiz: (lectureIndex: number, hide: boolean) => void;
  onSetAll: (hide: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  // Checkboxes are framed as "Show" (checked = visible to this batch). A batch
  // in a lecture's excludedBatches does NOT see it.
  const isLectureShown = (l: any) =>
    !((l?.excludedBatches ?? []) as any[]).map(String).includes(batchId);
  const isQuizShown = (l: any) =>
    !((l?.quiz?.excludedBatches ?? []) as any[]).map(String).includes(batchId);
  const shownLectures = lectures.filter(isLectureShown).length;
  const withQuiz = lectures.filter((l) => l?.quiz);
  const shownQuizzes = withQuiz.filter(isQuizShown).length;

  if (!lectures.length) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        <Button
          size="small"
          color="inherit"
          onClick={() => setOpen((o) => !o)}
          startIcon={
            <Iconify icon={open ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'} />
          }
        >
          Content shown to this batch
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
          {shownLectures}/{lectures.length} lectures
          {withQuiz.length ? ` · ${shownQuizzes}/${withQuiz.length} quizzes` : ''} shown
        </Typography>
        {open && (
          <>
            <Button size="small" color="inherit" onClick={() => onSetAll(true)}>
              Hide all
            </Button>
            <Button size="small" onClick={() => onSetAll(false)}>
              Show all
            </Button>
          </>
        )}
      </Stack>
      <Collapse in={open} unmountOnExit>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, pl: 1 }}>
          Tick what this batch can open. Unticked content shows to them as “Opening soon”.
        </Typography>
        <Stack spacing={0.5} sx={{ mt: 1, pl: 1 }}>
          {lectures.map((l, li) => (
            <Stack
              key={l?._id || li}
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ sm: 'center' }}
              spacing={1}
              sx={{ py: 0.5, borderBottom: '1px dashed', borderColor: 'divider' }}
            >
              <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap title={l?.title}>
                {li + 1}. {l?.title || `Lecture ${li + 1}`}
              </Typography>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={isLectureShown(l)}
                    onChange={(e) => onToggleLecture(li, !e.target.checked)}
                  />
                }
                label={<Typography variant="caption">Show lecture</Typography>}
              />
              {l?.quiz ? (
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={isQuizShown(l)}
                      onChange={(e) => onToggleQuiz(li, !e.target.checked)}
                    />
                  }
                  label={<Typography variant="caption">Show quiz</Typography>}
                />
              ) : null}
            </Stack>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

export default function ProgramBatchesField() {
  const { control, watch, setValue } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: 'batches' });
  const values = watch();
  const batches = useMemo(() => (values?.batches || []) as any[], [values]);
  const lectures = useMemo(() => (values?.lectures || []) as any[], [values]);

  // Instructors available to run a cohort. Assigning one here is what makes the
  // program appear in that person's instructor portal.
  const [instructors, setInstructors] = useState<
    { _id: string; name?: string; email?: string }[]
  >([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axiosInstance.get(APIS.INSTRUCTORS.LIST);
        const list = res?.data?.data ?? res?.data ?? [];
        if (alive) setInstructors(Array.isArray(list) ? list : []);
      } catch {
        if (alive) setInstructors([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Add/remove a batch id from a lecture's (or its quiz's) excludedBatches.
  const toggleExclusion = useCallback(
    (path: string, current: any[] | undefined, batchId: string, hide: boolean) => {
      const arr = (current ?? []).map(String);
      const next = hide
        ? Array.from(new Set([...arr, batchId]))
        : arr.filter((x) => x !== batchId);
      setValue(path, next, { shouldDirty: true });
    },
    [setValue]
  );

  // Hide (or show) EVERY lecture + quiz for one batch in a single click — makes
  // setting up an "opening soon" cohort one action.
  const setAllForBatch = useCallback(
    (batchId: string, hide: boolean) => {
      lectures.forEach((l, li) => {
        toggleExclusion(`lectures.${li}.excludedBatches`, l?.excludedBatches, batchId, hide);
        if (l?.quiz) {
          toggleExclusion(
            `lectures.${li}.quiz.excludedBatches`,
            l?.quiz?.excludedBatches,
            batchId,
            hide
          );
        }
      });
    },
    [lectures, toggleExclusion]
  );

  // id -> option, so any batch can render a label for a selected student.
  const [known, setKnown] = useState<Record<string, UserOption>>({});

  // Returns the SAME object when nothing new was learned. Without this, every
  // search result would change `known`'s identity and churn the pickers.
  const addKnown = useCallback((opts: UserOption[]) => {
    setKnown((prev) => {
      let changed = false;
      const next = { ...prev };
      opts.forEach((o) => {
        if (o?._id && !prev[o._id]?.name && (o.name || o.email)) {
          next[o._id] = o;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, []);

  // Resolve labels for students already assigned to any batch (edit mode).
  const assignedKey = JSON.stringify(batches.map((b) => b?.students || []));
  useEffect(() => {
    const ids = Array.from(
      new Set(batches.flatMap((b) => ((b?.students || []) as string[]).map(String)))
    );
    const missing = ids.filter((id) => !known[id]);
    if (!missing.length) return undefined;
    let cancelled = false;
    (async () => {
      const resolved = await Promise.all(
        missing.map((id) =>
          axiosInstance
            .get(APIS.USERS.BYId(id))
            .then((res) => {
              const d = res.data?.data ?? res.data;
              return d ? ({ _id: id, name: d.name, email: d.email } as UserOption) : { _id: id };
            })
            .catch(() => ({ _id: id } as UserOption))
        )
      );
      if (!cancelled) addKnown(resolved);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedKey, addKnown]);

  // Flag a student sitting in more than one batch — almost always a mistake,
  // and it makes the per-batch analysis ambiguous.
  const duplicates = useMemo(() => {
    const seen = new Map<string, number>();
    batches.forEach((b) =>
      ((b?.students || []) as string[]).forEach((s) =>
        seen.set(String(s), (seen.get(String(s)) || 0) + 1)
      )
    );
    return new Set(
      Array.from(seen.entries())
        .filter(([, n]) => n > 1)
        .map(([id]) => id)
    );
  }, [batches]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="subtitle1">Batches</Typography>
          <Typography variant="caption" color="text.secondary">
            Cohorts of this program. Batches group students and compare quiz results per cohort — and
            each batch can hide specific lectures/quizzes (they show as “Opening soon” to that batch).
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Iconify icon="eva:plus-fill" />}
          onClick={() => append({ ...emptyBatch })}
        >
          Add batch
        </Button>
      </Stack>

      {fields.length === 0 && (
        <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'background.neutral' }}>
          <Typography variant="body2" color="text.secondary">
            No batches yet. Without batches the program behaves as before — everyone in “Assign to
            users” sees it, and quiz results are reported as one group.
          </Typography>
        </Card>
      )}

      <Stack spacing={2}>
        {fields.map((field, i) => {
          const students = (batches[i]?.students || []) as string[];
          const capacity = Number(batches[i]?.capacity) || 0;
          const over = capacity > 0 && students.length > capacity;
          return (
            <Card key={field.id} sx={{ p: 2.5 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Iconify icon="eva:people-outline" sx={{ color: 'primary.main' }} />
                <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                  {batches[i]?.name || `Batch ${i + 1}`}
                </Typography>
                <Chip
                  size="small"
                  label={`${students.length}${capacity ? ` / ${capacity}` : ''} students`}
                  color={over ? 'error' : 'default'}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={batches[i]?.active !== false}
                      onChange={(e) => setValue(`batches.${i}.active`, e.target.checked)}
                    />
                  }
                  label="Active"
                />
                <Tooltip title="Remove batch">
                  <IconButton size="small" color="error" onClick={() => remove(i)}>
                    <Iconify icon="eva:trash-2-outline" />
                  </IconButton>
                </Tooltip>
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
                }}
              >
                <TextField
                  size="small"
                  label="Batch name"
                  value={batches[i]?.name || ''}
                  onChange={(e) => setValue(`batches.${i}.name`, e.target.value)}
                  required
                />
                <TextField
                  size="small"
                  label="Name (Arabic)"
                  value={batches[i]?.name_ar || ''}
                  onChange={(e) => setValue(`batches.${i}.name_ar`, e.target.value)}
                  inputProps={{ dir: 'rtl' }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="Start date"
                  InputLabelProps={{ shrink: true }}
                  value={batches[i]?.startDate || ''}
                  onChange={(e) => setValue(`batches.${i}.startDate`, e.target.value)}
                />
                <TextField
                  size="small"
                  type="date"
                  label="End date"
                  InputLabelProps={{ shrink: true }}
                  value={batches[i]?.endDate || ''}
                  onChange={(e) => setValue(`batches.${i}.endDate`, e.target.value)}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Capacity"
                  helperText={over ? 'Over capacity' : 'Blank = unlimited'}
                  error={over}
                  value={batches[i]?.capacity ?? ''}
                  onChange={(e) => setValue(`batches.${i}.capacity`, e.target.value)}
                />
                {/* Who runs this cohort. This is what unlocks the instructor
                    portal: the grading screens scope on batches.instructor. */}
                <TextField
                  select
                  size="small"
                  label="Instructor"
                  helperText="Sees this cohort in the instructor portal and grades its work"
                  value={batches[i]?.instructor || ''}
                  onChange={(e) => setValue(`batches.${i}.instructor`, e.target.value)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {instructors.map((ins) => (
                    <MenuItem key={ins._id} value={ins._id}>
                      {ins.name || ins.email || ins._id}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Divider sx={{ my: 2 }} />

              <BatchStudentPicker
                students={students}
                known={known}
                addKnown={addKnown}
                duplicates={duplicates}
                onChange={(ids) => setValue(`batches.${i}.students`, ids)}
              />

              {batches[i]?._id ? (
                <BatchContentVisibility
                  batchId={String(batches[i]._id)}
                  lectures={lectures}
                  onToggleLecture={(li, hide) =>
                    toggleExclusion(
                      `lectures.${li}.excludedBatches`,
                      lectures[li]?.excludedBatches,
                      String(batches[i]._id),
                      hide
                    )
                  }
                  onToggleQuiz={(li, hide) =>
                    toggleExclusion(
                      `lectures.${li}.quiz.excludedBatches`,
                      lectures[li]?.quiz?.excludedBatches,
                      String(batches[i]._id),
                      hide
                    )
                  }
                  onSetAll={(hide) => setAllForBatch(String(batches[i]._id), hide)}
                />
              ) : (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 2, display: 'block' }}
                >
                  Save the program first to choose which lectures/quizzes this batch sees.
                </Typography>
              )}
            </Card>
          );
        })}
      </Stack>

      {duplicates.size > 0 && (
        <Typography variant="caption" color="warning.main" sx={{ mt: 1.5, display: 'block' }}>
          Highlighted students are in more than one batch — their quiz results would be counted
          under whichever batch they were in at submit time.
        </Typography>
      )}
    </Box>
  );
}
